<template>
  <ModalComponent
    :model-value="showRef"
    :close="() => handleCloseModal()"
    :max-width="maxWidth"
    :style-class-contain="styleClassContain"
    :modal-class="showRef ? 'date-picker-modal' : ''"
  >
    <ModalHeader
      style="z-index: 10"
      :title="
        getComponentUiText(
          'app-vendor-assessment-change-recipient-assessment-modal-title-1',
        )
      "
      @close="handleCloseModal"
    />

    <div class="tw-pt-0 tw-flex tw-flex-col">
      <RecipientsDetails
        ref="recipientsDetailsRef"
        v-model="recipientsDetailsModal"
        @update:is-disabled="(val) => (isRequiredRecipientDetails = val)"
      />
    </div>

    <!-- Action button -->
    <div
      :class="`tw-px-[40px] tw-pb-[40px] tw-flex sm:tw-flex-row tw-flex-col tw-justify-between tw-gap-[15px] sm:tw-gap-[0px]`"
    >
      <button
        class="tw-bg-[#fff] hover:!tw-bg-[#F7553C] hover:tw-text-[#fff] tw-text-[#F7553C] tw-border-[2px] tw-border-solid tw-border-[#F7553C] tw-py-[12px] tw-px-[18px] tw-text-[15px] tw-rounded-[27px] tw-w-fit tw-h-[39px] tw-leading-[100%] tw-font-medium tw-flex tw-items-center tw-justify-center"
        @click="handleCloseModal"
      >
        {{
          getComponentUiText(
            "app-menu-submenu-home-setup-wizard-modal-button-cancel-1",
          )
        }}
      </button>

      <div class="tw-flex tw-gap-[10px] tw-items-center">
        <button
          class="tw-flex tw-items-center tw-bg-[#DBDBDB] tw-text-[#B7B7B7] tw-py-[12px] tw-px-[18px] tw-text-[15px] tw-rounded-[27px] tw-h-[39px] tw-leading-[100%] tw-font-medium"
          :class="`${'!tw-bg-[#387DFF] tw-text-white hover:!tw-bg-[#0069d9]'}`"
          @click="putChecklistRequested"
        >
          <div
            v-if="isLoadingAPI"
            class="tw-h-[39px] tw-w-[39px] tw-text-[#FFF]"
          >
            <!-- eslint-disable vue/no-unused-refs -->
            <LottieAnimation
              ref="anim"
              :animation-data="ProgressAnimation"
              :auto-play="true"
              :loop="true"
            />
          </div>
          <span v-else>
            {{
              getComponentUiText(
                "app-vendor-assessment-change-recipient-assessment-btn-resend-1",
              )
            }}
          </span>
        </button>
      </div>
    </div>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalHeader from "@/components/common/modalHeader.vue";
import { getComponentUiText } from "@/utils/utilities";
import { computed, onMounted, onUnmounted, ref } from "vue";
import ModalComponent from "@/components/common/modalComponent.vue";
import store from "@/store";
import RecipientsDetails from "./recipientDetails.vue";
import type RecipientsDetailsType from "./recipientDetails.vue";
import ProgressAnimation from "../../assets/animation/progress_dark.json";
import { LottieAnimation } from "lottie-web-vue";
import { Vendor } from "@/services/apiRoutes/routes";
import { showErrorAPI, showSuccessToast } from "@/services/apiRoutes";

const props = defineProps({
  checklistRequestedId: {
    type: Number,
    required: true,
    default: null,
  },
  emailGrantId: {
    type: Number,
    required: true,
    default: null,
  },
  providerId: {
    type: Number,
    required: true,
    default: null,
  },
  offererOrgName: {
    type: String,
    required: false,
    default: "",
  },
  recipientEmail: {
    type: String,
    required: false,
    default: "",
  },
  validFrom: {
    type: String,
    required: false,
    default: "",
  },
  validUntil: {
    type: String,
    required: false,
    default: "",
  },
});

let showRef = ref(true);
const recipientsDetailsRef = ref<InstanceType<
  typeof RecipientsDetailsType
> | null>(null);
const maxWidth = 65;
const isLoadingAPI = ref(false);
const isRequiredRecipientDetails = ref(false);
const recipientsDetailsModal = ref({
  name: props.offererOrgName,
  email: props.recipientEmail,
  expiryDate: props.validUntil,
});

const screenWidth = ref(window.innerWidth);

const updateWidth = () => {
  screenWidth.value = window.innerWidth;
};

onMounted(() => {
  window.addEventListener("resize", updateWidth);
});

onUnmounted(() => {
  window.removeEventListener("resize", updateWidth);
});

const emit = defineEmits(["update:show", "update:data", "update:close"]);

const handleCloseModal = () => {
  showRef.value = false;
  emit("update:show", showRef.value);
};

const putChecklistRequested = async () => {
  isLoadingAPI.value = true;
  const params = {
    checklistRequestedId: props.checklistRequestedId,
    emailGrantId: props.emailGrantId,
    providerId: props.providerId,
  };
  const body = {
    recipientEmail: recipientsDetailsModal.value.email,
    offererOrgName: recipientsDetailsModal.value.name,
    validUntil: recipientsDetailsModal.value.expiryDate,
  };
  try {
    const validate = await recipientsDetailsRef.value?.isCheckValid();
    if (!validate) {
      isLoadingAPI.value = false;
      return;
    }
    if (!props.offererOrgName && !props.recipientEmail && !props.validUntil) {
      const recipientName = recipientsDetailsModal.value.name.split(" ");
      const recipientFirstName = recipientName[0];
      const recipientLastName = recipientName.slice(1).join(" ");
      await store.dispatch("Vendor/postChecklistRequestedGetSecret", {
        payload: {
          checklistRequestedId: props.checklistRequestedId,
          recipientEmail: recipientsDetailsModal.value.email,
          recipientFirstName,
          recipientLastName,
          offererOrgName: recipientsDetailsModal.value.name,
          validUntil: recipientsDetailsModal.value.expiryDate,
        },
        providerId: props.providerId,
        checklistRequestedId: props.checklistRequestedId,
      });
    } else {
      await store.dispatch("Vendor/updateChecklistRequestedEmailGrant", {
        params,
        body,
      });
    }
    showSuccessToast(
      getComponentUiText(
        "app-vendor-assessment-change-recipient-assessment-success-1",
      ),
    );
    handleCloseModal();
  } catch (error) {
    isLoadingAPI.value = false;
    showErrorAPI(error, Vendor.putChecklistRequestedEmailGrant);
  }
};

const styleClassContain = computed(() => ({
  "border-radius": "6px",
  "box-shadow": "0px 0px 4px 0px rgba(0, 0, 0, 0.05)",
  overflow: screenWidth.value >= 600 ? "unset !important" : "auto",
}));
</script>

<style scoped>
textarea::placeholder {
  color: #878787;
  font-size: 13px;
  font-family: Poppins;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

input::placeholder {
  color: #878787;
  font-size: 13px;
  font-family: Poppins;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}
</style>
